import { useCallback } from 'react';
import { useAuthStore } from '@/entities/auth';
import { Languages, SettingStoreUserValues, Theme } from './types';
import { DEFAULT_LANGUAGE, initialUserValues, useSettingsActions, useSettingsStore } from './store';

/**
 * Главный хук доступа к настройкам.
 *
 * Определяет текущего пользователя через `useAuthStore` и возвращает
 * его персональные настройки вместе со всеми bound-экшенами.
 * Если пользователь не аутентифицирован — используется `'anonymous'` как userId
 * и применяются `initialUserValues` по умолчанию.
 *
 * Возвращает:
 * - `user` / `userId` — текущий пользователь и его строковый ID
 * - `settings` — объект `SettingStoreUserValues` для текущего пользователя
 * - `language` — глобальный язык интерфейса
 * - `theme` — применённая тема оформления
 * - `dialogsPage` — настройки страницы диалогов
 * - `tables` — настройки всех таблиц
 * - `sidebar` — настройки боковой панели
 * - `dashboardPresets` — пользовательские дашборды-пресеты и применённый из них
 * - `apiHost` — текущий URL бэкенда (глобальный)
 * - Все экшены: `setTheme`, `setDialogsSettings`, `setSidebarSettings`,
 *   `setTableSettings`,
 *   `setLanguage`, `setApiHost`, `resetApiHost`
 */
export const useSettings = () => {
  const user = useAuthStore((state) => state.user);
  const userId = user?.id != null ? String(user.id) : null;

  const userSettings = useSettingsStore((store) => {
    if (!userId) {
      return initialUserValues;
    }
    return store.settings[userId] ?? initialUserValues;
  });

  const {
    setLanguage,
    setTheme,
    setDialogsSettings,
    setSidebarSettings,
    setTableSettings,
    saveTablePreset,
    updateTablePreset,
    applyTablePreset,
    enterTableView,
    deleteTablePreset,
    renameTablePreset,
    setTablePresetPinned,
    clearActiveTablePreset,
    exitTableSelection,
    setDashboardQualitySettings,
    createDashboardPreset,
    updateDashboardPreset,
    applyDashboardPreset,
    clearActiveDashboardPreset,
    deleteDashboardPreset,
    setDashboardPresetPinned,
    setDashboardChartTableSettings,
    setApiHost,
    resetApiHost,
  } = useSettingsActions();

  const apiHost = useSettingsStore((store) => store.apiHost);
  const globalLanguage = useSettingsStore((store) => store.language);
  const theme = useSettingsStore((store) => store.theme);

  const safeUserId = userId ?? 'anonymous';

  const boundSetLanguage = useCallback((language: Languages) => setLanguage(language), [setLanguage]);

  // Тему передаём с настоящим userId, а не с safeUserId: набор 'anonymous' в профиль не уедет,
  // и запись выбора в него была бы молчаливой потерей
  const boundSetTheme = useCallback((newTheme: Theme) => setTheme(userId, newTheme), [userId, setTheme]);

  const boundSetDialogsSettings = useCallback(
    (settings: Partial<SettingStoreUserValues['dialogsPage']>) => setDialogsSettings(safeUserId, settings),
    [safeUserId, setDialogsSettings],
  );

  const boundSetSidebarSettings = useCallback(
    (settings: Partial<SettingStoreUserValues['sidebar']>) => setSidebarSettings(safeUserId, settings),
    [safeUserId, setSidebarSettings],
  );

  const boundSetTableSettings = useCallback(
    (tableName: string, settings: Partial<SettingStoreUserValues['tables'][string]>) =>
      setTableSettings(safeUserId, tableName, settings),
    [safeUserId, setTableSettings],
  );

  const boundSetDashboardQualitySettings = useCallback(
    (settings: Partial<SettingStoreUserValues['dashboardQuality']>) =>
      setDashboardQualitySettings(safeUserId, settings),
    [safeUserId, setDashboardQualitySettings],
  );

  return {
    user,
    userId: safeUserId,
    settings: userSettings,
    language: globalLanguage ?? DEFAULT_LANGUAGE,
    theme,
    dialogsPage: userSettings.dialogsPage,
    tables: userSettings.tables,
    sidebar: userSettings.sidebar,
    dashboardQuality: userSettings.dashboardQuality,
    dashboardPresets: userSettings.dashboardPresets,
    apiHost,
    setLanguage: boundSetLanguage,
    setTheme: boundSetTheme,
    setDialogsSettings: boundSetDialogsSettings,
    setSidebarSettings: boundSetSidebarSettings,
    setTableSettings: boundSetTableSettings,
    saveTablePreset,
    updateTablePreset,
    applyTablePreset,
    enterTableView,
    deleteTablePreset,
    renameTablePreset,
    setTablePresetPinned,
    clearActiveTablePreset,
    exitTableSelection,
    setDashboardQualitySettings: boundSetDashboardQualitySettings,
    createDashboardPreset,
    updateDashboardPreset,
    applyDashboardPreset,
    clearActiveDashboardPreset,
    deleteDashboardPreset,
    setDashboardPresetPinned,
    setDashboardChartTableSettings,
    setApiHost,
    resetApiHost,
  };
};
